import { createReducer, on } from '@ngrx/store';
import { produce } from 'immer';
import moment from 'moment';
import { CustomerType } from 'src/app/model/customer';
import { Advertisment, CommunicationMechanism, Menu, OnlineReservationCategory, RestaurantsList } from 'src/app/model/login-response.dto';
import { ReservationTagCategory } from 'src/app/model/reservation-tag';
import { Source } from 'src/app/model/source';
import { environment } from 'src/environments/environment';
import * as AppActions from '../actions/app.actions';
import { AppSettings } from '@model/app-settings';
import { FontSize } from 'src/app/settings-form/settings-form.component';

export const appFeatureKey = 'app';

export interface State {
  sources: Source[],
  restaurantId: number,
  restaurantName: string,
  isOffline: boolean,
  alert: string | null,
  menus: Menu[],
  onlineCategories: OnlineReservationCategory[],
  customerTypes: CustomerType[],
  tags: ReservationTagCategory[] | null,
  communication: CommunicationMechanism | null,
  serviceStopDate: Date | null,
  ads: Advertisment | null,
  extraStatuses: string[],
  minTelephoneLength: number,
  queueHttpRequests: boolean,
  appSettings: AppSettings,
  editingAppSettings: AppSettings,
  toast: string | null,
  restaurants: { display: string, value: string }[],
}

export const initialState: State = {
  sources: [],
  restaurantId: 0,
  restaurantName: '',
  isOffline: false,
  alert: null,
  menus: [],
  onlineCategories: [],
  customerTypes: [],
  tags: [],
  communication: null,
  serviceStopDate: null,
  extraStatuses: [],
  minTelephoneLength: 0,
  queueHttpRequests: true,
  ads: null,
  appSettings: {
    fontSize: {
      reservationList: FontSize.Default,
      reservationForm: FontSize.Default,
      tablesPlan: FontSize.Default,
    }
  },
  editingAppSettings: {
    fontSize: {
      reservationList: FontSize.Default,
      reservationForm: FontSize.Default,
      tablesPlan: FontSize.Default,
    }
  },
  toast: null,
  restaurants: [],
};

export const reducer = createReducer(
  initialState,

  on(AppActions.loadApps, state => state),
  on(AppActions.loadAppsSuccess, (state, action) => state),
  on(AppActions.loadAppsFailure, (state, action) => state),
  on(AppActions.loadSourcesSuccess, (state, action) => {
    return produce(state, (draftState) => {
      draftState.sources = action.sources;
    });
  }),
  on(AppActions.updateSource, (state, action) => {
    return produce(state, (draftState) => {

      if (action.source == null || action.source.id == null) {
        return;
      }

      const existingSourceIndex = state.sources.findIndex(source => source.id === action.source.id);

      if (existingSourceIndex < 0) {
        return;
      }

      draftState.sources = Object.assign([], draftState.sources, { [existingSourceIndex]: action.source });
    });
  }),
  on(AppActions.setMenus, (state, action) => {
    return produce(state, (draftState) => {
      draftState.menus = action.menus;
    });
  }),
  on(AppActions.setOnlineCategories, (state, action) => {
    return produce(state, (draftState) => {
      draftState.onlineCategories = action.onlineCategories;
    });
  }),
  on(AppActions.setCustomerTypes, (state, action) => {
    return produce(state, (draftState) => {
      draftState.customerTypes = action.customerTypes;
    });
  }),
  on(AppActions.setRestaurantId, (state, action) => {
    return produce(state, (draftState) => {
      draftState.restaurantId = action.restaurantId;
    });
  }),
  on(AppActions.setRestaurantName, (state, action) => {
    return produce(state, (draftState) => {
      draftState.restaurantName = action.restaurantName;
    });
  }),
  on(AppActions.setIsOffline, (state, action) => {
    return produce(state, (draftState) => {
      draftState.isOffline = action.isOffline;
    });
  }),
  on(AppActions.showAlert, (state, action) => {
    return produce(state, (draftState) => {
      draftState.alert = action.alert;
    });
  }),
  on(AppActions.hideAlert, (state, action) => {
    return produce(state, (draftState) => {
      draftState.alert = null;
    });
  }),
  on(AppActions.loadTagsSuccess, (state, action) => {
    return produce(state, (draftState) => {
      draftState.tags = action.tags;
    });
  }),
  on(AppActions.setCommunication, (state, action) => {
    return produce(state, (draftState) => {
      draftState.communication = action.communication;
    });
  }),
  on(AppActions.setServiceStopDate, (state, action) => {
    return produce(state, (draftState) => {
      draftState.serviceStopDate = action.stopDate;
    });
  }),  
  on(AppActions.setExtraStatuses, (state, action) => {
    return produce(state, (draftState) => {
      draftState.extraStatuses = action.extraStatuses;
    });
  }),
  on(AppActions.setMinTelephoneLength, (state, action) => {
    return produce(state, (draftState) => {
      draftState.minTelephoneLength = action.minTelephoneLength;
    });
  }),
  on(AppActions.setQueueHttpRequests, (state, action) => {
    return produce(state, (draftState) => {
      draftState.queueHttpRequests = action.value;
    });
  }),
  on(AppActions.setAds, (state, action) => {
    return produce(state, (draftState) => {
      draftState.ads = action.ads;
    });
  }),  
  on(AppActions.setAppSettings, (state, action) => {
    return produce(state, (draftState) => {
      draftState.appSettings = action.appSettings;
    });
  }),  
  on(AppActions.setEditingAppSettings, (state, action) => {
    return produce(state, (draftState) => {
      draftState.editingAppSettings = action.appSettings;
    });
  }),  
  on(AppActions.showToast, (state, action) => {
    return produce(state, (draftState) => {
      draftState.toast = action.message;
    });
  }),
  on(AppActions.setRestaurants, (state, action) => {
    return produce(state, (draftState) => {
      draftState.restaurants = action.restaurants;
    });
  }),
);
